import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Zap, LogOut, LayoutDashboard, Crown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import OptimizedImage from "@/components/OptimizedImage";
import logo from "@/assets/logo.webp";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut, subscription } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const links = [
    { label: t("nav.features"), href: "/#funzionalita" },
    { label: t("nav.pricing"), href: "/#prezzi" },
    { label: t("nav.blog"), href: "/blog" },
    { label: t("nav.about"), href: "/chi-siamo" },
  ];

  const guideLinks = [
    { label: t("nav.guide1"), href: "/link-in-bio-creator" },
    { label: t("nav.guide2"), href: "/link-in-bio-instagram" },
    { label: t("nav.guide3"), href: "/monetizzare-instagram" },
    { label: t("nav.guide4"), href: "/vendere-prodotti-digitali-instagram" },
    { label: t("nav.guide5"), href: "/link-in-bio-gratis" },
    { label: t("nav.guide6"), href: "/prenotazioni-online" },
  ];

  const [guideOpen, setGuideOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 font-extrabold text-xl tracking-tight text-foreground">
            <img src={logo} alt="TuttiIMieiLink" className="w-12 h-12 rounded-md object-contain" width={48} height={48} loading="eager" decoding="async" fetchPriority="high" />
            TuttiIMieiLink
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a key={l.href} href={l.href} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {l.label}
              </a>
            ))}
            <div className="relative" onMouseEnter={() => setGuideOpen(true)} onMouseLeave={() => setGuideOpen(false)}>
              <button className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t("nav.guides")}
              </button>
              <AnimatePresence>
                {guideOpen && (
                  <motion.div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-card border border-border rounded-xl shadow-elevated p-2 z-50"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.15 }}
                  >
                    {guideLinks.map((g) => (
                      <a key={g.href} href={g.href} className="block px-3 py-2.5 text-sm text-foreground hover:bg-secondary rounded-lg transition-colors">
                        {g.label}
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <div className="flex items-center gap-2">
                {subscription?.subscribed && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/15 text-accent text-xs font-bold border border-accent/25">
                    <Crown className="w-3 h-3" /> PRO
                  </span>
                )}
                <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  {t("nav.dashboard")}
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => signOut()}>
                  <LogOut className="w-3.5 h-3.5" />
                  {t("nav.logout")}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/auth")}>
                  {t("nav.login")}
                </Button>
                <Button variant="default" size="sm" className="gap-1.5 rounded-full bg-gradient-accent text-accent-foreground border-0 hover:opacity-90" onClick={() => navigate("/auth?tab=register")}>
                  <Zap className="w-3.5 h-3.5" />
                  {t("nav.register")}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden relative w-10 h-10 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            <AnimatePresence mode="wait">
              {mobileOpen ? (
                <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <X className="w-5 h-5" />
                </motion.div>
              ) : (
                <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <Menu className="w-5 h-5" />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        </div>
      </nav>

      {/* Mobile menu — rendered OUTSIDE nav to avoid iOS backdrop-filter stacking context bug */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="md:hidden fixed inset-0 top-16 bg-foreground/20 backdrop-blur-sm z-[60]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMobile}
            />
            <motion.div
              className="md:hidden fixed top-16 left-0 right-0 bottom-0 z-[70] bg-background overflow-y-auto"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="px-6 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)]">
                <div className="space-y-1">
                  {links.map((l, i) => (
                    <motion.a key={l.href} href={l.href} className="flex items-center px-3 py-3 rounded-lg text-base font-medium text-foreground hover:bg-secondary transition-colors" onClick={closeMobile} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                      {l.label}
                    </motion.a>
                  ))}
                  <p className="px-3 pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("nav.guides")}</p>
                  {guideLinks.map((g, i) => (
                    <motion.a key={g.href} href={g.href} className="flex items-center px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-secondary transition-colors" onClick={closeMobile} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: (links.length + i) * 0.05 }}>
                      {g.label}
                    </motion.a>
                  ))}
                  <LanguageSwitcher mobile />
                </div>

                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  {user ? (
                    <>
                      <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => { navigate("/dashboard"); closeMobile(); }}>
                        <LayoutDashboard className="w-4 h-4" />
                        {t("nav.dashboard")}
                        {subscription?.subscribed && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/15 text-accent text-xs font-bold border border-accent/25 ml-auto">
                            <Crown className="w-3 h-3" /> PRO
                          </span>
                        )}
                      </Button>
                      <Button variant="outline" className="w-full justify-start gap-2" onClick={() => { signOut(); closeMobile(); }}>
                        <LogOut className="w-4 h-4" />
                        {t("nav.logout")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button className="w-full gap-2 rounded-full bg-gradient-accent text-accent-foreground border-0 hover:opacity-90" onClick={() => { navigate("/auth?tab=register"); closeMobile(); }}>
                        <Zap className="w-4 h-4" />
                        {t("nav.register")}
                      </Button>
                      <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => { navigate("/auth"); closeMobile(); }}>
                        {t("nav.login")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
